import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { VerifiedFactsApproval } from "./MonthlyReport";
import PlatformModels from "./PlatformModels";

// The question library and the run settings. Wording changes are recorded,
// because a reworded question breaks the trend it sits in.
export default function QuestionLibrary({ onChanged }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [keys, setKeys] = useState(null);
  const [keyDraft, setKeyDraft] = useState({});
  const [keyResult, setKeyResult] = useState({});
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ text: "", category: "A", branded: false, scope: "" });

  const load = () => {
    api.get("/admin/ai-monitor/questions").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/admin/ai-monitor/settings").then((r) => setSettings(r.data)).catch(() => setSettings(null));
    api.get("/admin/ai-monitor/keys").then((r) => setKeys(r.data)).catch(() => setKeys(null));
  };

  useEffect(load, []);

  const save = async (q) => {
    setBusy(true);
    try {
      const res = await api.post("/admin/ai-monitor/questions", q);
      if (res.data.wording_changed) toast.info("Wording change recorded — earlier results stay attached.");
      else toast.success("Saved.");
      setDraft({ text: "", category: "A", branded: false, scope: "" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    const res = await api.delete("/admin/ai-monitor/questions", { params: { id } });
    toast.success(res.data.disabled ? res.data.detail : "Removed.");
    load();
  };

  const saveSettings = async (changes) => {
    setBusy(true);
    try {
      const res = await api.post("/admin/ai-monitor/settings", changes);
      setSettings(res.data);
      toast.success("Settings saved.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the settings.");
    } finally {
      setBusy(false);
    }
  };

  const saveKey = async (provider) => {
    setBusy(true);
    setKeyResult((r) => ({ ...r, [provider]: { checking: true } }));
    try {
      const res = await api.post("/admin/ai-monitor/keys", { provider, key: keyDraft[provider] });
      setKeyDraft((d) => ({ ...d, [provider]: "" }));
      const msg = res.data.model_changed
        ? `Key accepted. ${res.data.platform} will be asked with ${res.data.model}, chosen from the `
          + `${res.data.available.length} models your account offers`
          + (res.data.previous_model ? ` (${res.data.previous_model} is not among them).` : ".")
        : `Key accepted and stored. ${res.data.platform} will be asked with ${res.data.model}.`;
      setKeyResult((r) => ({ ...r, [provider]: { ok: msg } }));
      toast.success(msg);      load();
      onChanged?.();
    } catch (e) {
      const why = e?.response?.data?.detail
        || (e?.response ? `The server returned ${e.response.status}.` : "The server could not be reached.");
      setKeyResult((r) => ({ ...r, [provider]: { error: why } }));
      toast.error(why);
    } finally {
      setBusy(false);
    }
  };

  const removeKey = async (provider) => {
    await api.delete("/admin/ai-monitor/keys", { params: { provider } });
    toast.success("Key removed.");
    load();
  };

  if (!data || !settings) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="ai-question-library">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h4 className="font-bold text-navy text-sm">Scheduling and budget</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <label className="block">
            <span className="text-xs font-semibold text-slate-500">Frequency</span>
            <select value={settings.frequency} onChange={(e) => saveSettings({ frequency: e.target.value })}
                    className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-2"
                    data-testid="ai-setting-frequency">
              {["weekly", "fortnightly", "monthly"].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-500">Repeats per question</span>
            <select value={settings.repeats} onChange={(e) => saveSettings({ repeats: Number(e.target.value) })}
                    className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-2"
                    data-testid="ai-setting-repeats">
              {[1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-500">Monthly limit (£)</span>
            <Input type="number" defaultValue={settings.monthly_cap_gbp} data-testid="ai-setting-cap"
                   onBlur={(e) => saveSettings({ monthly_cap_gbp: Number(e.target.value) })} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-500">Day of month</span>
            <Input type="number" min={1} max={28} defaultValue={settings.run_day} data-testid="ai-setting-day"
                   onBlur={(e) => saveSettings({ run_day: Number(e.target.value) })} />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          {settings.platform_catalogue.map((p) => (
            <label key={p.name} className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={settings.platforms.includes(p.name)} disabled={!p.ready}
                     data-testid={`ai-platform-${p.name.toLowerCase()}`}
                     onChange={(e) => saveSettings({
                       platforms: e.target.checked ? [...settings.platforms, p.name]
                         : settings.platforms.filter((x) => x !== p.name),
                     })} />
              <span>
                {p.name}
                <span className="block text-[11px] text-slate-500">{p.blocked_reason || p.note}</span>
              </span>
            </label>
          ))}
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={settings.auto_run} data-testid="ai-setting-auto"
                 onChange={(e) => saveSettings({ auto_run: e.target.checked })} />
          Run automatically on the scheduled day (off means it only runs when you press the button)
        </label>

        <p className="text-xs text-slate-500 mt-3" data-testid="ai-cost-estimate">
          {settings.enabled_questions} questions enabled · estimated £{settings.estimated_cost_per_run.toFixed(2)} per run,
          £{settings.estimated_monthly_cost.toFixed(2)} a month at {settings.frequency} · £{settings.spend_this_month.toFixed(2)} spent
          this month against a £{settings.monthly_cap_gbp.toFixed(0)} limit. {settings.cost_basis}
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-provider-keys">
        <h4 className="font-bold text-navy text-sm">Platform API keys</h4>
        <p className="text-xs text-slate-500 mt-1">{keys?.note}</p>
        <div className="mt-3 space-y-3">
          {(keys?.providers || []).map((p) => (
            <div key={p.provider} className="flex flex-wrap items-end gap-2" data-testid={`ai-key-row-${p.provider}`}>
              <label className="flex-1 min-w-[260px]">
                <span className="text-xs font-semibold text-slate-500">
                  {p.label}{p.set ? " — stored" : ""}
                </span>
                <Input type="password" value={keyDraft[p.provider] || ""} autoComplete="off"
                       placeholder={p.set ? "Stored. Paste a new key to replace it." : "Paste the key"}
                       onChange={(e) => setKeyDraft((d) => ({ ...d, [p.provider]: e.target.value }))}
                       data-testid={`ai-key-input-${p.provider}`} />
                <span className="text-[11px] text-slate-400 mt-1 block">From {p.where}</span>
              </label>
              <Button size="sm" onClick={() => saveKey(p.provider)} disabled={busy || !keyDraft[p.provider]}
                      data-testid={`ai-key-save-${p.provider}`}>
                {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save className="mr-2" size={14} />}
                {p.set ? "Replace" : "Check and save"}
              </Button>
              {p.set && (
                <Button size="sm" variant="outline" onClick={() => removeKey(p.provider)}
                        data-testid={`ai-key-remove-${p.provider}`}>Remove</Button>
              )}
              {keyResult[p.provider] && (
                <p className={`w-full text-xs rounded-md px-2 py-1.5 ${
                  keyResult[p.provider].error ? "bg-red-50 text-red-700"
                    : keyResult[p.provider].ok ? "bg-emerald-50 text-emerald-700" : "text-slate-500"}`}
                   data-testid={`ai-key-result-${p.provider}`}>
                  {keyResult[p.provider].checking
                    ? "Checking the key with the provider…"
                    : keyResult[p.provider].error || keyResult[p.provider].ok}
                </p>
              )}
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Checked against the provider before being stored, held encrypted, and never returned to the browser.
        </p>
      </div>

      <PlatformModels settings={settings} onSaved={() => { load(); onChanged?.(); }} />

      <VerifiedFactsApproval onApproved={() => { load(); onChanged?.(); }} />

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h4 className="font-bold text-navy text-sm">Verified business facts</h4>
        <p className="text-xs text-slate-500 mt-1">
          What an answer about CASS is judged against. Keep it current — accuracy is assessed on these facts.
          A blank field is treated as unverifiable, never as untrue.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {["name", "trading_name", "short_name", "company_number", "vat_number", "incorporation_date",
            "former_name", "founder", "website", "email_domain", "address", "registered_office",
            "accreditations", "memberships"].map((k) => (
            <label key={k} className="block">
              <span className="text-xs font-semibold text-slate-500">{k.replace(/_/g, " ")}</span>
              <Input defaultValue={settings.profile[k]} data-testid={`ai-profile-${k}`}
                     onBlur={(e) => saveSettings({ profile: { ...settings.profile, [k]: e.target.value } })} />
            </label>
          ))}
        </div>
        {["services", "services_standalone_exception", "services_not_provided", "software_partners",
          "xero_status", "locations", "remote_support", "founder_biography", "other_people"].map((k) => (
          <label key={k} className="block mt-3">
            <span className="text-xs font-semibold text-slate-500">{k.replace(/_/g, " ")}</span>
            <Textarea rows={3} defaultValue={settings.profile[k]} data-testid={`ai-profile-${k}`}
                      onBlur={(e) => saveSettings({ profile: { ...settings.profile, [k]: e.target.value } })} />
          </label>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h4 className="font-bold text-navy text-sm">
          Questions — {data.unbranded} unbranded, {data.branded} branded · {data.target} target,{" "}
          {data.stretch} stretch
        </h4>
        <p className="text-xs text-slate-500 mt-1">
          Unbranded questions measure whether a platform finds CASS unprompted. Branded ones measure what it knows
          about CASS. They are never scored together.
        </p>
        <p className="text-xs text-slate-500 mt-1" data-testid="ai-scope-note">{data.scope_note}</p>

        <div className="mt-4 space-y-1.5">
          {data.questions.map((q) => (
            <div key={q.id} className="flex items-start gap-2 py-1.5 border-b border-slate-50" data-testid={`ai-question-${q.id}`}>
              <input type="checkbox" checked={q.enabled} className="mt-1"
                     onChange={(e) => save({ ...q, enabled: e.target.checked })}
                     data-testid={`ai-question-enabled-${q.id}`} />
              <span className="text-[11px] font-bold text-slate-400 mt-0.5 w-4">{q.category}</span>
              <input defaultValue={q.text} onBlur={(e) => e.target.value !== q.text && save({ ...q, text: e.target.value })}
                     className={`flex-1 text-sm bg-transparent outline-none ${q.enabled ? "text-slate-700" : "text-slate-400 line-through"}`}
                     data-testid={`ai-question-text-${q.id}`} />
              <select value={q.scope || "stretch"} onChange={(e) => save({ ...q, scope: e.target.value })}
                      className="text-[11px] font-semibold border border-slate-200 rounded-full px-2 py-0.5 text-slate-600"
                      data-testid={`ai-question-scope-${q.id}`}>
                {(data.scopes || ["target", "stretch"]).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {q.localised && (
                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">localised</span>
              )}
              {q.branded && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">branded</span>}
              {(q.revisions || []).length > 0 && (
                <span className="text-[10px] text-slate-400" title="Wording has changed">edited</span>
              )}
              <button onClick={() => remove(q.id)} className="text-slate-400 hover:text-red-500"
                      data-testid={`ai-question-delete-${q.id}`}><Trash2 size={13} /></button>
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[260px]">
            <span className="text-xs font-semibold text-slate-500">Add a question</span>
            <Input value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                   placeholder="Who provides…?" data-testid="ai-question-new" />
          </label>
          <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  className="text-sm border border-slate-200 rounded-md px-2 py-2" data-testid="ai-question-new-category">
            {Object.entries(data.categories).map(([k, label]) => <option key={k} value={k}>{k} · {label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={draft.branded} data-testid="ai-question-new-branded"
                   onChange={(e) => setDraft({ ...draft, branded: e.target.checked })} /> Branded
          </label>
          <select value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })}
                  title="Left on auto, the label follows the same rule as the rest: a question naming a place CASS serves is a target"
                  className="text-sm border border-slate-200 rounded-md px-2 py-2" data-testid="ai-question-new-scope">
            <option value="">label: auto</option>
            {(data.scopes || ["target", "stretch"]).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button size="sm" onClick={() => save(draft)} disabled={busy || !draft.text.trim()} data-testid="ai-question-add">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : <Plus className="mr-2" size={14} />} Add
          </Button>
        </div>
      </div>
    </div>
  );
}
